<template>
  <div v-if="loading" class="clearfix">
    <b-spinner class="float-right" label="Floated Right" />
  </div>
  <div v-else>
    <b-tabs v-model="activeTab" @input="onTabChange">
      <!-- ATENDIMENTO -->
      <b-tab>
        <template #title>
          <feather-icon icon="ClockIcon" />
          <span>Atendimentos </span>
        </template>
        <app-collapse type="margin">
          <app-collapse-item title="Atendimentos" :isVisible="true">
            <ProfissionalAtendimentoForm
              @disable-form-changed="handleDisableFormChange"
            />
          </app-collapse-item>
        </app-collapse>
      </b-tab>
      <b-tab
        v-if="showOdontogramaTab"
      >
        <template #title>
          <feather-icon icon="CheckIcon" />
          <span> ODONTOGRAMA </span>
        </template>
        <app-collapse type="margin">
          <app-collapse-item :isVisible="true" title="Odontogramas">
            <odontograma-form />
          </app-collapse-item>
        </app-collapse>
      </b-tab>
      <b-tab
        v-for="grupo in listaGrupo"
        :key="grupo.id"
        @click="loadProcedimentos(grupo)"
      >
        <template #title>
          <feather-icon icon="CheckIcon" />
          <span>{{ grupo.nome }}</span>
        </template>
        <app-collapse type="margin">
          <app-collapse-item :title="grupo.nome" :isVisible="true">
            <div v-if="loadingProcedimentos" class="text-center py-3">
              <b-spinner />
              <div class="mt-2">Carregando...</div>
            </div>
            <ProcedimentosForm
              v-else
              :procedimentos="listaProcedimentos"
              :grupo="grupo"
              @refresh-procedimentos="loadProcedimentos"
            />
          </app-collapse-item>
        </app-collapse>
      </b-tab>
      <!-- HISTORICO -->
      <!-- ABAS DINAMICAS -->
    </b-tabs>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { BSpinner, BTabs, BTab } from "bootstrap-vue";
import AppCollapse from "@core/components/app-collapse/AppCollapse.vue";
import AppCollapseItem from "@core/components/app-collapse/AppCollapseItem.vue";
import mixinsGlobal from "@/mixins";
import ProfissionalAtendimentoForm from "./ProfissionalAtendimentoForm.vue";
import ProcedimentosForm from "./ProcedimentosForm.vue";
import odontogramaForm from "@/views/modules/attendance/odontograma/OdontogramaForm.vue";

// Constants
const INACTIVITY_TIMEOUT = 3 * 60 * 1000;
const AUTO_SAVE_INTERVAL = 10000;
const TAB_CHANGE_CONFIG = {
  title: "Atenção",
  variant: "warning",
  solid: true,
  toaster: "b-toaster-top-center",
  noAutoHide: false,
};
const ESPECIALIDADE_ODONTOLOGISTA = 5;

// Activity events for inactivity tracking
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keypress",
  "touchmove",
  "click",
  "scroll",
];

export default {
  components: {
    AppCollapse,
    AppCollapseItem,
    BSpinner,
    BTabs,
    BTab,
    ProfissionalAtendimentoForm,
    ProcedimentosForm,
    odontogramaForm,
  },

  mixins: [mixinsGlobal],

  computed: {
    ...mapState("atendimentoProfissionalModule", {
      atendimento: (state) => state.atendimento,
    }),

    hasProcedimentos() {
      return this.listaProcedimentos && this.listaProcedimentos.length > 0;
    },

    showOdontogramaTab() {
      if (!this.atendimento || !this.atendimento.especialidade) return false;
      
      const currentId = this.atendimento.especialidade.id;
      if (currentId === ESPECIALIDADE_ODONTOLOGISTA) return true;

      const vinculadas = this.atendimento.especialidade.especialidadesVinculadas || [];
      return vinculadas.some(v => v.id === ESPECIALIDADE_ODONTOLOGISTA);
    },
  },

  data() {
    return {
      collapseType: "default",
      loading: false,
      loadingProcedimentos: false,
      activeTab: 0,
      previousTab: 0,
      disableForm: true,
      inactivityTimer: null,
      inactivityTimeout: INACTIVITY_TIMEOUT,
      lastActivity: Date.now(),
      listaGrupo: [],
      listaProcedimentos: [],
      ESPECIALIDADE_ODONTOLOGISTA,
    };
  },

  methods: {
    // Data loading methods
    async loadProcedimentos(grupo) {
      this.loadingProcedimentos = true;

      try {
        const grupoId = typeof grupo === "object" ? grupo.id : grupo;
        const obj = {
          grupo: grupoId,
          paciente: this.atendimento.registro.id,
          especialidade: this.atendimento.especialidade.id,
        };
        const response = await this.$store.dispatch(
          "atendimentoProfissionalModule/loadProcedimentosByPacienteAndGrupo",
          obj,
        );
        this.listaProcedimentos = response.data;
      } catch (error) {
        console.error("Error loading procedimentos:", error);
      } finally {
        this.loadingProcedimentos = false;
      }
    },

    async montarAbasDeGrupos() {
      try {
        const response = await this.$store.dispatch(
          "atendimentoProfissionalModule/montarAbasGrupo",
          this.$route.params.id,
        );
        this.listaGrupo = response.data;
      } catch (error) {
        console.error("Error loading grupos:", error);
      }
    },

    // Form control methods
    handleDisableFormChange(disableFormValue) {
      this.disableForm = disableFormValue;
    },

    // Tab management
    onTabChange(newTab) {
      if (!this.disableForm) {
        if (this.activeTab === this.previousTab) {
          return;
        }

        this.$bvToast.toast(
          "Por favor, salve ou cancele as alterações antes de trocar de aba.",
          TAB_CHANGE_CONFIG,
        );

        this.$nextTick(() => {
          this.activeTab = this.previousTab;
        });
        return;
      }

      this.previousTab = this.activeTab;
    },

    // Inactivity tracking methods
    setupActivityListeners() {
      ACTIVITY_EVENTS.forEach((event) => {
        document.addEventListener(event, this.resetInactivityTimer);
      });
    },

    removeActivityListeners() {
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, this.resetInactivityTimer);
      });
    },

    resetInactivityTimer() {
      this.lastActivity = Date.now();
    },

    clearInactivityTimer() {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
        this.inactivityTimer = null;
      }
    },
  },

  // Lifecycle hooks
  async mounted() {
    this.setupActivityListeners();
    await this.montarAbasDeGrupos();
  },

  beforeDestroy() {
    this.removeActivityListeners();
    this.clearInactivityTimer();
  },
};
</script>
